/**
 * How a clinic looks and where it can be found: two brand colours, a logo, the mark printed
 * behind its documents, and four contact links.
 *
 * Separate from `tenantProfile`, which is the clinic's *copy*. They are the same idea from
 * a distance and different in every way that matters here -- this is read on every app
 * boot, that one by a single page; this drives the theme, that one draws a page; and the
 * profile's save is a full replace that would blank whatever it did not send.
 *
 * The contact links are here rather than there for one more reason: the footer that draws
 * them renders on every page, so their data has to ride the request the app already makes
 * on boot and already caches per tenant.
 *
 * Everything is nullable, and null means the clinic has not chosen. The app falls back to
 * Carefull's own palette and mark rather than rendering unstyled, which is what lets a clinic
 * that has configured nothing still look finished; a link it has not set simply draws no
 * icon.
 */

import { validateEmail } from '../emailHelper';
import { isSupportedLanguage } from './language';

/**
 * What a clinic prints at before anybody touches the sliders.
 *
 * Mirrors `TenantBrandingEntity.DEFAULT_WATERMARK_OPACITY`/`_SCALE` and the column defaults
 * in changelog 019. Only ever used for the form's initial values, since the API answers with
 * the real numbers even for a clinic that has no branding row -- but opening the editor on
 * `1%` because that happens to be the bottom of the range would be a lie about what the
 * clinic's documents look like.
 */
export const WATERMARK_OPACITY_DEFAULT = 10;

export const WATERMARK_SCALE_DEFAULT = 60;

/** What GET /tenant-branding returns. */
export interface IBranding {
    /** `#RRGGBB`, or null for the platform's. */
    primaryColor: string | null;
    secondaryColor: string | null;
    /** The clinic's name, so chrome that prints it needs no second call. */
    displayName: string | null;
    /** Path to the logo, or null when the clinic has not uploaded one. */
    logoUrl: string | null;
    logoEtag: string | null;
    /**
     * Path to the icon the browser draws in the tab, or null when the clinic has not
     * uploaded one -- in which case the tab shows the logo above, which is what it showed
     * before a favicon could be uploaded at all.
     *
     * Public and clinic-named exactly as `logoUrl` is, and for the same reason: a browser
     * resolving `<link rel="icon">` sends no token.
     */
    faviconUrl: string | null;
    faviconEtag: string | null;
    /**
     * Path to the mark printed behind the clinic's evaluation documents, or null when it
     * has not uploaded one -- in which case the logo above is printed instead.
     *
     * Unlike `logoUrl` this route is **not public** and names no clinic: it is fetched with
     * the session's own token, because nothing but the branding editor ever looks at it.
     * So it cannot go straight into an `<img src>` -- see `fetchWatermarkFile`.
     */
    watermarkUrl: string | null;
    watermarkEtag: string | null;
    /**
     * How faint and how wide that mark is printed, as percentages.
     *
     * Never null, unlike everything else here: their columns carry defaults, because a
     * clinic that uploads a picture and touches nothing else still has to get a readable
     * document. They come back for a clinic with no watermark too, since that clinic is
     * printing its *logo* at exactly these settings.
     */
    watermarkOpacity: number;
    watermarkScale: number;
    /** Full `https://` addresses, exactly as the admin saved them. */
    instagramUrl: string | null;
    facebookUrl: string | null;
    /** A phone number, not a link -- see {@link whatsAppLink}. */
    whatsappNumber: string | null;
    mapsUrl: string | null;
    /**
     * Where a reply to one of the clinic's emails goes. The one field here the app never
     * draws: it is sent as the `Reply-To` header on every email the backend sends, so a
     * patient answering one reaches the clinic instead of the platform's noreply. It comes
     * back only so the editor can show an admin what is saved.
     */
    contactEmail: string | null;
    /**
     * The language new accounts at this clinic start in, and the one a signed-out visitor on
     * its own domain is shown. **Null means the clinic has not chosen**, and that is a state
     * the app acts on rather than a gap to fill in: it is what leaves a visitor's own browser
     * language alone. The platform's Romanian fallback is applied when an email is composed,
     * never stored here.
     */
    defaultLanguage: string | null;
}

/**
 * What PUT /tenant-branding takes. Blank means "not set": for a colour that is a reset to
 * the platform's, for a link it is taking the icon down.
 */
export interface BrandingRequest {
    primaryColor: string;
    secondaryColor: string;
    instagramUrl: string;
    facebookUrl: string;
    whatsappNumber: string;
    mapsUrl: string;
    contactEmail: string;
    /** Blank clears it, like every field above and unlike the two below. */
    defaultLanguage: string;
    /**
     * The two watermark settings, as numbers rather than the strings the draft holds.
     *
     * Blank does *not* mean "not set" for these, unlike every field above: the API treats
     * an absent value as "leave it alone", because their columns are NOT NULL and there is
     * no state to clear them to. The editor always sends both, so that path is the API's
     * backstop against an older client rather than something this form relies on.
     */
    watermarkOpacity: number;
    watermarkScale: number;
}

/** The draft the editor holds. Nulls arrive as empty strings, as a controlled input needs. */
export interface BrandingDraft {
    primaryColor: string;
    secondaryColor: string;
    instagramUrl: string;
    facebookUrl: string;
    whatsappNumber: string;
    mapsUrl: string;
    contactEmail: string;
    defaultLanguage: string;
    /** Strings, like every other number in a form here -- coercing per keystroke makes a
     *  field impossible to clear, and the sliders write through the same setter as the
     *  text inputs. `toBrandingRequest` is where they become numbers again. */
    watermarkOpacity: string;
    watermarkScale: string;
}

export const toBrandingDraft = (branding: IBranding | undefined): BrandingDraft => ({
    primaryColor: branding?.primaryColor ?? '',
    secondaryColor: branding?.secondaryColor ?? '',
    instagramUrl: branding?.instagramUrl ?? '',
    facebookUrl: branding?.facebookUrl ?? '',
    whatsappNumber: branding?.whatsappNumber ?? '',
    mapsUrl: branding?.mapsUrl ?? '',
    contactEmail: branding?.contactEmail ?? '',
    defaultLanguage: branding?.defaultLanguage ?? '',
    watermarkOpacity: String(branding?.watermarkOpacity ?? WATERMARK_OPACITY_DEFAULT),
    watermarkScale: String(branding?.watermarkScale ?? WATERMARK_SCALE_DEFAULT),
});

export const toBrandingRequest = (draft: BrandingDraft): BrandingRequest => ({
    primaryColor: draft.primaryColor.trim(),
    secondaryColor: draft.secondaryColor.trim(),
    instagramUrl: draft.instagramUrl.trim(),
    facebookUrl: draft.facebookUrl.trim(),
    whatsappNumber: draft.whatsappNumber.trim(),
    mapsUrl: draft.mapsUrl.trim(),
    contactEmail: draft.contactEmail.trim(),
    defaultLanguage: draft.defaultLanguage.trim(),
    watermarkOpacity: Number(draft.watermarkOpacity),
    watermarkScale: Number(draft.watermarkScale),
});

/**
 * The same rule the API enforces, so the editor can mark a field before the round trip
 * rather than after. Empty passes: that is how a colour is reset.
 */
export const isValidBrandColor = (value: string): boolean =>
    value.trim().length === 0 || /^#[0-9A-Fa-f]{6}$/.test(value.trim());

/**
 * The API's link rule, mirrored.
 *
 * The `https://` prefix is not house style: these values become `href` attributes, and
 * requiring a scheme is what keeps a `javascript:` URL out of the footer. The host is
 * deliberately unchecked -- `fb.me`, `m.me` and `maps.app.goo.gl` are all real, and a
 * whitelist would refuse a link that works. Empty passes: that is how a link is removed.
 */
export const isValidSocialUrl = (value: string): boolean =>
    value.trim().length === 0 || /^https:\/\/\S{3,290}$/.test(value.trim());

/**
 * WhatsApp is given as a phone number rather than a link, because an admin knows their own
 * number and should not have to know the `wa.me` format. Punctuation people actually type
 * is allowed through; {@link whatsAppLink} is what strips it.
 */
export const isValidWhatsAppNumber = (value: string): boolean =>
    value.trim().length === 0 || /^\+?[0-9][0-9 ()\-]{5,28}$/.test(value.trim());

/**
 * The reply-to address, checked with the app's own `validateEmail` rather than a rule of
 * this file's own — the API validates it with `@Email`, and there is no second opinion
 * about what an address looks like worth maintaining here.
 *
 * Empty passes, which is how an admin takes the reply-to off again. `validateEmail` rejects
 * an empty string, so that case is answered before it is asked.
 */
export const isValidContactEmail = (value: string): boolean =>
    value.trim().length === 0 || validateEmail(value.trim());

/**
 * The same rule the API's `Language.OPTIONAL_PATTERN` applies, so a value the button accepts
 * is never one the server then refuses.
 *
 * Empty passes, which is how a clinic stops having a default -- the same reason the pattern
 * on the server side makes its whole body optional. A picker cannot produce anything else
 * today; this is here so the schema can say what the field means rather than trusting it.
 */
export const isValidDefaultLanguage = (value: string): boolean =>
    value.trim().length === 0 || isSupportedLanguage(value.trim());

/**
 * `+40 775 626 322` -> `https://wa.me/40775626322`, or null when there is no number.
 *
 * wa.me wants digits and nothing else -- no `+`, no spaces, no punctuation -- so everything
 * else is dropped here rather than asked of the admin. Composed in the front end rather
 * than stored, so the editor can still show them what they typed.
 */
export const whatsAppLink = (number: string | null | undefined): string | null => {
    const digits: string = (number ?? '').replace(/\D/g, '');

    return digits.length === 0 ? null : `https://wa.me/${digits}`;
};
